import React, { Children, useLayoutEffect, useRef, useState } from 'react';

export function placeMasonry(heights, columns, gap) {
  const bottoms = Array(columns).fill(0);
  const positions = heights.map(height => {
    const column = bottoms.indexOf(Math.min(...bottoms));
    const top = bottoms[column];
    bottoms[column] += height + gap;
    return { column, top };
  });
  return { positions, height: Math.max(0, ...bottoms) - (heights.length ? gap : 0) };
}

// Observe the actual card box, not an img that may not exist until lazy loading.
// Reposition the same DOM nodes when previews/full images change their height.
export default function MasonryGrid({ children, className = '', gap = 20 }) {
  const hostRef = useRef(null);
  const [layout, setLayout] = useState(null);
  const items = Children.toArray(children);

  useLayoutEffect(() => {
    const host = hostRef.current;
    const measure = () => {
      const columns = Math.max(1, Number(getComputedStyle(host).getPropertyValue('--masonry-columns')) || 3);
      const next = { ...placeMasonry(Array.from(host.children, item => item.offsetHeight), columns, gap), columnStep: (host.clientWidth + gap) / columns };
      setLayout(previous => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(host);
    Array.from(host.children).forEach(item => observer?.observe(item));
    host.addEventListener('load', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      host.removeEventListener('load', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [children, gap]);

  return <div ref={hostRef} className={`masonry-grid ${className}`} style={{ position: 'relative', height: layout ? layout.height : undefined, '--masonry-gap': `${gap}px` }}>
    {items.map((child, index) => {
      const position = layout?.positions[index];
      return <div key={child.key} className="masonry-grid-item" style={{ position: position ? 'absolute' : 'relative', top: position?.top, left: position ? position.column * layout.columnStep : undefined }}>{child}</div>;
    })}
  </div>;
}
